import {React, ui, icons, Router} from 'lib';
import {formatPrice} from 'utils';
import {useRemoteData, Res, useChildUrl, S3Image, useSelected, useInventoryMode} from 'shared';

import {useStore, useBarcodeQuery, useModelCodeQuery, ScannerButton} from './base';
import {ItemDetail, ItemDetailHeader, StoreInfoList} from './model';

const itemPath = 'staff/get_items';
type Item = ArrayItem<Res<typeof itemPath>>;

const STOCK_INFO_VISIBLE_KEY = 'staff:item_dialog_stock_info_visible';


export const ItemDialog = ({barcode, onClose, onActionDone}: {
    barcode: string | null;
    onClose(): void;
    onActionDone?(): void;
}): React.ReactElement => {
    const store = useStore();
    const [selectedAmount, , selectedExists] = useSelected(barcode ?? '');
    const [inventoryMode] = useInventoryMode();

    const {data} = useRemoteData({
        path: itemPath,
        request: {
            store_id: store.id,
            barcodes: barcode ? [barcode] : [],
        },
        halt: !barcode,
    });

    const item: Item | undefined = data?.[0];

    const modelCodeQuery = useModelCodeQuery();
    const openModel = () => {
        if (!item) return;
        onClose();
        modelCodeQuery.replace(item.product.model.code, {barcode: null});
    };

    const [stockInfoVisible, setStockInfoVisible] = React.useState(
        () => localStorage.getItem(STOCK_INFO_VISIBLE_KEY) === '1');
    const toggleStockInfo = () => setStockInfoVisible(v => {
        const next = !v;
        localStorage.setItem(STOCK_INFO_VISIBLE_KEY, next ? '1' : '0');
        return next;
    });

    const disclosure = ui.useDisclosure({
        isOpen: !!barcode,
        onClose,
    });

    return <ui.Modal {...disclosure} size="3xl" closeOnOverlayClick={false}>
        <ui.ModalOverlay />
        <ui.ModalContent>
            <ui.ModalHeader textAlign="center">
                {barcode}
            </ui.ModalHeader>
            <ui.ModalCloseButton />

            <ui.ModalBody>
                {item && <ui.Stack spacing={4}>
                    <ui.HStack
                        spacing={4}
                        align="center"
                        cursor="pointer"
                        onClick={openModel}
                        _hover={{bg: 'gray.50'}}
                        p={2}
                        borderRadius="md"
                    >
                        {item.product.model.images[0] && <ui.Box width="120px" flexShrink={0}>
                            <S3Image
                                base={item.product.model.images[0].base_url}
                                processor="sd256"
                            />
                        </ui.Box>}
                        <ui.Box>
                            <ui.Heading size="md">
                                {item.product.model.name}
                            </ui.Heading>
                            <ui.Text fontSize="sm" mt={1}>
                                {item.product.model.code} / {item.product.domestic_code}
                            </ui.Text>
                            <ui.Text fontSize="sm" mt={1}>
                                {item.product.color} / {item.size}
                            </ui.Text>
                        </ui.Box>
                    </ui.HStack>

                    <ui.Box textAlign="center">
                        {item.product.sale_price === item.product.model.price
                            ? <ui.Text fontWeight="bold">
                                {formatPrice(item.product.model.price)}
                            </ui.Text>
                            : <>
                                <ui.Text fontWeight="bold" textDecoration="line-through">
                                    {formatPrice(item.product.model.price)}
                                </ui.Text>
                                <ui.Text color="red" fontWeight="bold">
                                    <ui.Text as="span" fontSize="xs">
                                        {item.product.discount_rate}%OFF
                                    </ui.Text>
                                    <ui.Text as="span" ml={1}>
                                        {formatPrice(item.product.sale_price)}
                                    </ui.Text>
                                </ui.Text>
                            </>
                        }
                    </ui.Box>

                    <ui.Table size="sm">
                        <ui.Thead>
                            <ItemDetailHeader selectable={true} hideSizeCol={true} />
                        </ui.Thead>
                        <ui.Tbody>
                            <ItemDetail
                                item={item}
                                selectable={true}
                                hideSizeCol={true}
                                hideBarcode={true}
                            />
                        </ui.Tbody>
                    </ui.Table>

                    <ui.HStack
                        cursor="pointer"
                        onClick={toggleStockInfo}
                        borderBottom="1px solid #ddd"
                        pb={1}
                        mt={6}
                        mb={2}
                    >
                        <ui.Text fontSize="sm" color="gray.600" textTransform="uppercase" letterSpacing="wider">Stock Info</ui.Text>
                        <ui.Spacer />
                        {stockInfoVisible ? <icons.ChevronDownIcon /> : <icons.ChevronRightIcon />}
                    </ui.HStack>
                    {stockInfoVisible && <StoreInfoList modelCode={item.product.model.code} />}
                </ui.Stack>}

                {!item && <ui.Box py={10}>
                    {!data && <ui.Center><ui.Spinner /></ui.Center>}
                    {data && <ui.Text textAlign="center" color="gray.500">
                        該当するアイテムが見つかりませんでした
                    </ui.Text>}
                </ui.Box>}
            </ui.ModalBody>

            <ui.ModalFooter>
                <ui.Box mr="auto">
                    <ScannerButton label="Scan" onOpen={disclosure.onClose} />
                </ui.Box>
                <ui.Button onClick={disclosure.onClose}>
                    Close
                </ui.Button>
                {item && (inventoryMode ? selectedExists : selectedAmount > 0) && onActionDone && <ui.Button
                    onClick={onActionDone}
                    colorScheme="blue"
                    ml={3}
                >
                    Action
                </ui.Button>}
            </ui.ModalFooter>
        </ui.ModalContent>
    </ui.Modal>;
};


export const useItemDialog = (disabled?: boolean): {
    dialog: React.ReactElement;
    isOpen: boolean;
} => {
    const barcodeQuery = useBarcodeQuery();
    const history = Router.useHistory();
    const actionUrl = useChildUrl('action');
    const barcode = disabled ? null : barcodeQuery.value;
    return {
        isOpen: !!barcode,
        dialog: <ItemDialog
            barcode={barcode}
            onClose={() => barcodeQuery.replace(null)}
            onActionDone={() => {
                barcodeQuery.replace(null);
                history.push(actionUrl);
            }}
        />,
    };
};
